// ─── SSE CLIENT ────────────────────────────────────────────────────────────
// Format-agnostic Server-Sent Events client.
// Connects to a POST endpoint that returns text/event-stream.
// Parses SSE lines and calls onEvent({ event, data }) for each message.
// Does NOT interpret event types — callers decide what to do.

import { getAuthCallbacks } from '../../auth/apiFetch.js';

/**
 * @param {object} opts
 * @param {string} opts.url        — endpoint URL
 * @param {object} opts.body       — JSON request body
 * @param {function} opts.onEvent  — called with { event: string|null, data: any } for each SSE message
 * @param {function} opts.onError  — called with Error on failure
 * @param {function} opts.onDone   — called when stream ends
 * @param {object} [opts.headers]  — extra headers (e.g., auth)
 * @returns {{ abort: function }}  — call abort() to cancel the stream
 */
export function streamSSE({ url, body, onEvent, onError, onDone, headers = {} }) {
  const ctrl = new AbortController();

  (async () => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const { onSessionExpired, onTenantDenied } = getAuthCallbacks();
        if (res.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_tenants');
          localStorage.removeItem('auth_tenant_id');
          onSessionExpired?.();
          throw new Error('SESSION_EXPIRED');
        }
        if (res.status === 403) {
          const body = await res.clone().json().catch(() => ({}));
          if (body.error_code === 'TENANT_ACCESS_DENIED') {
            localStorage.removeItem('auth_tenant_id');
            onTenantDenied?.();
            throw new Error('TENANT_DENIED');
          }
        }
        throw new Error(`SSE request failed: ${res.status} ${res.statusText}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData += (currentData ? '\n' : '') + line.slice(5).trim();
          } else if (line === '') {
            if (currentData) {
              let parsed;
              try {
                parsed = JSON.parse(currentData);
              } catch (_) {
                parsed = currentData;
              }
              onEvent({ event: currentEvent, data: parsed });
            }
            currentEvent = null;
            currentData = '';
          }
        }
      }

      if (currentData) {
        let parsed;
        try {
          parsed = JSON.parse(currentData);
        } catch (_) {
          parsed = currentData;
        }
        onEvent({ event: currentEvent, data: parsed });
      }

      onDone?.();
    } catch (err) {
      if (err.name === 'AbortError') {
        onDone?.();
      } else {
        onError?.(err);
      }
    }
  })();

  return { abort: () => ctrl.abort() };
}

/**
 * Stream SSE from a GET endpoint (no request body).
 * Used for pipeline progress streaming where task_id is in the URL.
 *
 * @param {object} opts
 * @param {string} opts.url        — endpoint URL
 * @param {function} opts.onEvent  — called with { event: string|null, data: any }
 * @param {function} opts.onError  — called with Error on failure
 * @param {function} opts.onDone   — called when stream ends
 * @param {object} [opts.headers]  — extra headers (e.g., auth)
 * @returns {{ abort: function }}
 */
export function streamSSEGet({ url, onEvent, onError, onDone, headers = {} }) {
  const ctrl = new AbortController();

  (async () => {
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: { ...headers },
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const { onSessionExpired, onTenantDenied } = getAuthCallbacks();
        if (res.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_tenants');
          localStorage.removeItem('auth_tenant_id');
          onSessionExpired?.();
          throw new Error('SESSION_EXPIRED');
        }
        if (res.status === 403) {
          const body = await res.clone().json().catch(() => ({}));
          if (body.error_code === 'TENANT_ACCESS_DENIED') {
            localStorage.removeItem('auth_tenant_id');
            onTenantDenied?.();
            throw new Error('TENANT_DENIED');
          }
        }
        throw new Error(`SSE request failed: ${res.status} ${res.statusText}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData += (currentData ? '\n' : '') + line.slice(5).trim();
          } else if (line === '') {
            if (currentData) {
              let parsed;
              try { parsed = JSON.parse(currentData); } catch (_) { parsed = currentData; }
              onEvent({ event: currentEvent, data: parsed });
            }
            currentEvent = null;
            currentData = '';
          }
        }
      }

      if (currentData) {
        let parsed;
        try { parsed = JSON.parse(currentData); } catch (_) { parsed = currentData; }
        onEvent({ event: currentEvent, data: parsed });
      }

      onDone?.();
    } catch (err) {
      if (err.name === 'AbortError') { onDone?.(); } else { onError?.(err); }
    }
  })();

  return { abort: () => ctrl.abort() };
}
